import { supabase } from './supabase';

export interface WorkflowStepLibraryItem {
  id: string;
  business_id: string;
  title: string;
  description?: string | null;
  category?: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by?: string | null;
}

export async function getWorkflowStepLibrary(businessId: string): Promise<WorkflowStepLibraryItem[]> {
  const { data, error } = await supabase
    .from('workflow_step_library')
    .select('*')
    .eq('business_id', businessId)
    .eq('is_active', true)
    .order('category', { ascending: true })
    .order('title', { ascending: true });

  if (error) {
    console.error('Error fetching workflow step library:', error);
    // If table doesn't exist, return empty array
    if (error.message?.includes('relation') && error.message?.includes('does not exist')) {
      console.warn('Workflow step library table not found. Returning empty array.');
      return [];
    }
    throw error;
  }

  return data || [];
}



export async function createWorkflowStepLibraryItem(
  businessId: string,
  title: string,
  description?: string,
  category?: string
): Promise<WorkflowStepLibraryItem> {
  const { data, error } = await supabase
    .from('workflow_step_library')
    .insert({
      business_id: businessId,
      title,
      description,
      category,
      created_by: (await supabase.auth.getUser()).data.user?.id
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating workflow step library item:', error);
    // If table doesn't exist, return mock created item
    if (error.message?.includes('does not exist')) {
      console.warn('Workflow step library table not found. Returning mock created item.');
      const now = new Date().toISOString();
      return {
        id: Math.random().toString(36).substring(2, 11),
        business_id: businessId,
        title,
        description: description || null,
        category: category || null,
        is_active: true,
        created_at: now,
        updated_at: now,
        created_by: (await supabase.auth.getUser()).data.user?.id || null
      };
    }
    throw error;
  }

  return data;
}

export async function updateWorkflowStepLibraryItem(
  id: string,
  updates: Partial<Pick<WorkflowStepLibraryItem, 'title' | 'description' | 'category' | 'is_active'>>
): Promise<WorkflowStepLibraryItem> {
  const { data, error } = await supabase
    .from('workflow_step_library')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating workflow step library item:', error);
    // If table doesn't exist, just log and return a mock updated item
    if (error.message?.includes('does not exist')) {
      console.warn('Workflow step library table not found. Cannot update item.');
      // In a real scenario, we'd maintain state, but for now just throw
      throw new Error('Cannot update items when table does not exist. Please run migrations first.');
    }
    throw error;
  }

  return data;
}

export async function deleteWorkflowStepLibraryItem(id: string): Promise<void> {
  const { error } = await supabase
    .from('workflow_step_library')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting workflow step library item:', error);
    // If table doesn't exist, just log
    if (error.message?.includes('does not exist')) {
      console.warn('Workflow step library table not found. Cannot delete item.');
      throw new Error('Cannot delete items when table does not exist. Please run migrations first.');
    }
    throw error;
  }
}

// Initialize default workflow steps for a business if they don't have any
export async function initializeDefaultWorkflowSteps(businessId: string): Promise<void> {
  // No default steps - businesses will create their own
  return;
}